<!-- Loading bar -->
<spri-loading-bar />

<!-- Custom Navigation -->
<nav
  class="custom-sidenav dark:bg-granpy-900 relative z-10 bg-white print:hidden"
  [class.collapsed]="sidenavStore.isCollapsed()"
>
  <!-- Navigation header - Logo in sidebar -->
  <div
    aria-label="Go to landing page"
    class="logo-section flex cursor-pointer items-center px-4 py-3"
    role="button"
    tabindex="0"
    (click)="onLogoClick()"
    (keyup.enter)="onLogoClick()"
    (keyup.space)="onLogoClick()"
  >
    <!-- Single full logo with text sliding -->
    <div class="brand-container">
      <img
        class="brand-logo full-logo"
        height="40"
        priority
        width="200"
        [alt]="tenantContextService.tenantTitle()"
        [ngSrc]="tenantContextService.logoUrl() || '/images/logo/steadman-logo.svg'"
      />
    </div>
  </div>

  <!-- Navigation Items -->
  <ul class="nav-list px-3" role="menu">
    @for (item of navigation?.default || []; track item.id || item.title) {
      <!-- Basic item: visible only with permission -->
      @if (!item.children) {
        <li
          *appHasPermission="'nav:' + item.id"
          class="nav-li mb-1"
          [class.active]="sidenavStore.openItemId() === item.id"
        >
          <button
            class="nav-item flex w-full cursor-pointer items-center rounded-lg p-3 transition-colors hover:bg-primary hover:text-white dark:hover:bg-gray-800"
            matTooltip="{{ sidenavStore.isCollapsed() ? item.title : '' }}"
            matTooltipPosition="right"
            role="menuitem"
            type="button"
            [matTooltipDisabled]="!sidenavStore.isCollapsed()"
            [routerLink]="item.link || null"
            [routerLinkActive]="item.link ? 'text-on-primary bg-primary' : ''"
            (click)="onItemClick(item)"
          >
            <!-- Icon -->
            <span class="icon flex h-6 w-6 flex-shrink-0 items-center justify-center text-current">
              <mat-icon class="h-6 w-6 text-current" [svgIcon]="item.icon" />
            </span>

            <!-- Text -->
            <span class="ml-4 flex-1 text-wrap" [attr.aria-hidden]="sidenavStore.isCollapsed() ? 'true' : null">
              <span class="label text-sm font-medium uppercase">{{ item.title }}</span>
            </span>
          </button>
        </li>
      }

      <!-- Collapsable item: render only if any child is permitted or parent permission exists -->
      @if (item.children && (hasAnyChildPermission(item) || canAccess('nav:' + item.id))) {
        <li class="nav-li mb-1" [class.active]="sidenavStore.openItemId() === item.id">
          <button
            class="nav-item flex w-full cursor-pointer items-center rounded-lg p-3 transition-colors hover:bg-primary hover:text-white dark:hover:bg-gray-800"
            matTooltip="{{ sidenavStore.isCollapsed() ? item.title : '' }}"
            matTooltipPosition="right"
            role="menuitem"
            type="button"
            [attr.aria-expanded]="sidenavStore.openItemId() === item.id && !sidenavStore.isCollapsed()"
            [matTooltipDisabled]="!sidenavStore.isCollapsed()"
            [ngClass]="{ 'bg-primary text-on-primary': hasActiveChild(item) }"
            (click)="onItemClick(item)"
          >
            <!-- Icon -->
            <span class="icon flex h-6 w-6 flex-shrink-0 items-center justify-center text-current">
              <mat-icon class="h-6 w-6 text-current" [svgIcon]="item.icon" />
            </span>

            <!-- Text -->
            <span class="ml-4 flex-1 text-wrap" [attr.aria-hidden]="sidenavStore.isCollapsed() ? 'true' : null">
              <span class="label text-sm font-medium uppercase">{{ item.title }}</span>
            </span>

            <!-- Chevron -->
            <span class="chevron-wrap flex-shrink-0" [attr.aria-hidden]="sidenavStore.isCollapsed() ? 'true' : null">
              <mat-icon
                class="chevron h-4 w-4 text-current transition-transform"
                svgIcon="heroicons_solid:chevron-down"
                [class.rotate-180]="sidenavStore.openItemId() === item.id"
              />
            </span>
          </button>

          <!-- Submenu -->
          @if (sidenavStore.openItemId() === item.id && !sidenavStore.isCollapsed()) {
            <div class="submenu pl-10">
              @for (child of item.children; track child.id || child.title) {
                @if (child.externalLink) {
                  <a
                    *appHasPermission="'nav:' + item.id + '/' + child.id"
                    class="submenu-item block rounded p-2 text-sm uppercase text-gray-600 transition-colors hover:bg-primary hover:text-white dark:text-gray-400 dark:hover:text-gray-100"
                    rel="noopener noreferrer"
                    [href]="child.link || '#'"
                    [target]="child.target || '_blank'"
                    (click)="onExternalLinkClick($event, child); onLeafNavigate()"
                  >
                    {{ child.title }}
                  </a>
                } @else {
                  <a
                    *appHasPermission="'nav:' + item.id + '/' + child.id"
                    class="submenu-item block rounded p-2 text-sm uppercase text-gray-600 transition-colors hover:bg-primary hover:text-white dark:text-gray-400 dark:hover:text-gray-100"
                    [routerLink]="child.link"
                    [routerLinkActive]="'text-primary'"
                    (click)="onLeafNavigate()"
                  >
                    {{ child.title }}
                  </a>
                }
              }
            </div>
          }
        </li>
      }
    }
  </ul>
</nav>

<!-- Wrapper -->
<div class="wrapper flex w-full min-w-0 flex-auto flex-col">
  <!-- Header -->
  <div
    class="header-divider relative z-49 flex h-[66px] w-full flex-0 items-center bg-white px-[30px] py-[2px] shadow-sm dark:bg-gray-900 print:hidden"
  >
    <!-- Navigation toggle button - positioned on the dividing line -->
    <button
      class="toggle-button absolute -left-6 top-1/2 z-[99999] -translate-y-1/2 transform rounded-full border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
      mat-icon-button
      type="button"
      [title]="sidenavStore.isCollapsed() ? 'Expand Navigation' : 'Collapse Navigation'"
      (click)="toggleNavigation()"
    >
      <img
        alt="Navigation Toggle"
        class="toggle-icon h-5 w-5"
        height="20"
        width="20"
        [ngSrc]="'/icons/navigation-icon.svg'"
      />
    </button>

    <!-- Title, Search, and User Profile Row -->
    <div class="header-content" [class.header-content--no-search]="!headerConfig?.showSearch">
      <!-- Page Title -->
      <h1 class="page-title">
        {{ headerConfig?.title || currentPageTitle }}
      </h1>

      <!-- Header Search (optional) -->
      @if (headerConfig?.showSearch) {
        <form
          autocomplete="new-password"
          class="header-search-wrapper"
          [formGroup]="searchForm"
          (ngSubmit)="$event.preventDefault()"
        >
          <div
            class="header-search-input-container"
            role="button"
            tabindex="0"
            [attr.aria-label]="'Open search panel'"
            [class.is-active]="showFilterPanel"
            (click)="onSearchInputClick()"
            (keydown.enter)="onSearchContainerEnter($event)"
          >
            <img alt="Search" class="header-search-icon" height="24" src="/icons/search-icon.svg" width="24" />
            <input
              autocomplete="new-password"
              class="header-search-input"
              formControlName="query"
              type="text"
              [attr.aria-label]="headerConfig?.searchPlaceholder || 'Search'"
              [placeholder]="headerConfig?.searchPlaceholder || 'Search'"
              (input)="onSearchInputChange($event)"
              (keydown.enter)="performSearch(searchForm.value.query || ''); $event.preventDefault()"
            />
          </div>

          <!-- Search Panel with Backdrop -->
          @if (showFilterPanel) {
            <div
              aria-hidden="true"
              class="search-backdrop"
              (click)="closeSearchPanel()"
              (keydown.enter)="closeSearchPanel()"
              (keydown.space)="closeSearchPanel(); $event.preventDefault()"
            ></div>
            <div
              aria-label="Search filters panel"
              class="filter-panel spri-search-panel compact"
              role="group"
              tabindex="0"
              (click)="$event.stopPropagation()"
              (keydown.enter)="$event.stopPropagation()"
              (keydown.space)="$event.stopPropagation()"
              (mousedown)="onFilterPanelMousedown($event)"
            >
              <div class="header-search-input-container panel-field is-active">
                <img alt="Search" class="header-search-icon" height="24" src="/icons/search-icon.svg" width="24" />
                <input
                  autocomplete="new-password"
                  class="header-search-input"
                  formControlName="query"
                  type="text"
                  [placeholder]="headerConfig?.searchPlaceholder || 'Search'"
                  (input)="onSearchInputChange($event)"
                  (keydown.enter)="performSearch(searchForm.value.query || ''); $event.preventDefault()"
                />
                <button
                  class="trailing-action-btn"
                  mat-icon-button
                  type="button"
                  [attr.aria-label]="searchForm.get('query')?.value ? 'Clear search' : 'Close search panel'"
                  (click)="
                    searchForm.get('query')?.value ? clearSearchInput() : closeSearchPanel(); $event.stopPropagation()
                  "
                >
                  <mat-icon>close</mat-icon>
                </button>
              </div>

              @if (headerConfig?.searchFilters && headerConfig.searchFilters.length > 0) {
                <ng-template #filterChip let-filterGroup>
                  <button
                    class="filter-button"
                    mat-button
                    [class.active]="getFilterCount(filterGroup.key) > 0"
                    [class.can-clear]="getFilterCount(filterGroup.key) > 0"
                    [matMenuTriggerFor]="menuRef"
                    (mouseenter)="onFilterChipEnter(filterGroup.key)"
                    (mouseleave)="onFilterChipLeave()"
                  >
                    <span class="label-text">{{ getFilterLabel(filterGroup) }}</span>
                    @if (!(getFilterCount(filterGroup.key) > 0 && hoveredFilterKey === filterGroup.key)) {
                      <img alt="Expand" class="icon-caret" height="16" src="/icons/arrow-down.svg" width="16" />
                    }
                    @if (getFilterCount(filterGroup.key) > 0 && hoveredFilterKey === filterGroup.key) {
                      <mat-icon
                        aria-label="Clear {{ filterGroup.label }}"
                        class="icon-clear"
                        role="button"
                        tabindex="0"
                        (click)="clearFilterGroup(filterGroup.key, $event)"
                        (keydown.enter)="clearFilterGroup(filterGroup.key, $event)"
                        (keydown.space)="clearFilterGroup(filterGroup.key, $event)"
                        >close</mat-icon
                      >
                    }
                  </button>

                  <mat-menu #menuRef="matMenu" xPosition="after" yPosition="below">
                    <div
                      aria-label="Filter options menu"
                      class="menu-shell"
                      role="group"
                      tabindex="0"
                      (click)="$event.stopPropagation()"
                      (keydown.enter)="$event.stopPropagation()"
                      (keydown.space)="$event.stopPropagation()"
                      (mousedown)="$event.stopPropagation()"
                    >
                      <div class="menu-search">
                        <mat-icon>search</mat-icon>
                        <input
                          autocomplete="new-password"
                          type="text"
                          [name]="'search-' + filterGroup.key"
                          [ngModelOptions]="{ standalone: true }"
                          [placeholder]="'Search ' + filterGroup.label.toLowerCase()"
                          [(ngModel)]="menuSearchText[filterGroup.key]"
                        />
                        <button class="clear" type="button" (click)="clearMenuSearch(filterGroup.key)">
                          <mat-icon>close</mat-icon>
                        </button>
                      </div>

                      <div class="menu-options">
                        @for (opt of getFilteredOptions(filterGroup); track opt.value ?? opt.label) {
                          <div class="option">
                            <mat-checkbox
                              [checked]="isFilterChecked(filterGroup.key, opt.value)"
                              (change)="toggleFilterOption(filterGroup.key, opt.value)"
                            >
                              {{ opt.label }}
                            </mat-checkbox>
                          </div>
                        }
                        @if (!getFilteredOptions(filterGroup).length) {
                          <div class="empty">No matches</div>
                        }
                      </div>
                    </div>
                  </mat-menu>
                </ng-template>

                <div class="filter-row">
                  @for (filterGroup of headerConfig.searchFilters; track filterGroup?.key) {
                    <spri-filter-chip-dropdown
                      [filterGroup]="filterGroup"
                      [isHovered]="hoveredFilterKey === filterGroup.key"
                      [isOpen]="openFilterKey === filterGroup.key"
                      [selectedValues]="selectedFilters[filterGroup.key] ?? []"
                      (clearFilter)="clearFilterGroup(filterGroup.key, $event)"
                      (hoverEnter)="onFilterChipEnter(filterGroup.key)"
                      (hoverLeave)="onFilterChipLeave()"
                      (selectionChange)="onFilterSelectionChange(filterGroup.key, $event)"
                      (toggleRequested)="onFilterToggle($event)"
                    />
                  }
                </div>
              }

              <div class="filter-actions">
                <button
                  class="search-cta"
                  type="button"
                  (click)="performSearch(searchForm.value.query || '')"
                  (keydown.enter)="performSearch(searchForm.value.query || '')"
                  (keydown.space)="performSearch(searchForm.value.query || '')"
                >
                  SEARCH
                </button>
                <button
                  class="reset-cta"
                  type="button"
                  (click)="onSearchReset()"
                  (keydown.enter)="onSearchReset()"
                  (keydown.space)="onSearchReset()"
                >
                  <img alt="Reset" class="reset-icon" height="16" src="/icons/reset-icon.svg" width="15" />
                  RESET
                </button>
              </div>
            </div>
          }
        </form>
      }

      <!-- User Profile -->
      @if (headerConfig?.showUserProfile) {
        <div class="header-user-wrapper">
          <spri-user />
        </div>
      }
    </div>
  </div>

  <!-- Content -->
  <div class="content-wrapper flex w-full flex-auto flex-col bg-white">
    <!-- *ngIf="true" hack is required here for router-outlet to work correctly.
           Otherwise, layout changes won't be registered and the view won't be updated! -->
    @if (true) {
      <div class="content-area flex h-full w-full min-w-0 flex-1 flex-col">
        <router-outlet />
      </div>
    }
  </div>

  <!-- Footer -->
  <div
    class="footer-divider relative flex h-14 w-full flex-0 items-center justify-end border-t border-gray-200 bg-white px-4 md:px-6 dark:border-gray-700 dark:bg-gray-900 print:hidden"
    [class.footer-divider--search-open]="showFilterPanel"
    [class.z-49]="!showFilterPanel"
  >
    <span class="text-sm font-medium text-gray-500 dark:text-gray-400"
      >Steadman Philippon Research Institute {{ tenantContextService.currentYear }} &middot; v{{ appVersion }}</span
    >
  </div>
</div>
